import { useEffect, useState, type ReactNode } from 'react';
import { CheckCircle2, AlertCircle, X } from 'lucide-react';
import { cn } from '@/lib/cn';

type ToastKind = 'success' | 'error';

interface Toast {
  id: number;
  kind: ToastKind;
  message: string;
}

// Set en lugar de Array: garantiza unicidad incluso con HMR.
// Módulos en Vite dev pueden re-ejecutarse; un Set sobrevive bien porque
// add/delete son idempotentes.
type Listener = (t: Toast) => void;
const listeners = new Set<Listener>();

export function toast(message: string, kind: ToastKind = 'success') {
  const t: Toast = { id: Date.now() + Math.random(), kind, message };
  listeners.forEach((l) => l(t));
}

/** Limpia listeners (uso interno / tests). */
export function __resetToastListeners() {
  listeners.clear();
}

export function ToastHost() {
  const [toasts, setToasts] = useState<Toast[]>([]);

  // Suscripción en useEffect + cleanup: clave para evitar duplicados con
  // React.StrictMode (mount/unmount/mount en dev) y HMR.
  useEffect(() => {
    const handle: Listener = (t) => {
      setToasts((prev) => [...prev, t]);
      setTimeout(() => {
        setToasts((prev) => prev.filter((x) => x.id !== t.id));
      }, 3500);
    };
    listeners.add(handle);
    return () => {
      listeners.delete(handle);
    };
  }, []);

  return (
    <div className="fixed bottom-4 right-4 z-50 flex flex-col gap-2 max-w-sm" data-testid="toast-host">
      {toasts.map((t) => (
        <div
          key={t.id}
          role="status"
          data-testid="toast"
          data-toast-kind={t.kind}
          className={cn(
            'card flex items-center gap-3 py-3 px-4 shadow-lg',
            t.kind === 'success' && 'border-accent',
            t.kind === 'error' && 'border-danger',
          )}
        >
          {t.kind === 'success' ? (
            <CheckCircle2 size={20} className="text-accent flex-shrink-0" />
          ) : (
            <AlertCircle size={20} className="text-danger flex-shrink-0" />
          )}
          <p className="text-sm flex-1">{t.message}</p>
          <button
            onClick={() => setToasts((prev) => prev.filter((x) => x.id !== t.id))}
            className="text-text-dim hover:text-text"
            aria-label="Cerrar notificación"
          >
            <X size={16} />
          </button>
        </div>
      ))}
    </div>
  );
}

export function ToastHostSingleton({ children }: { children: ReactNode }) {
  return (
    <>
      {children}
      <ToastHost />
    </>
  );
}